import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  AlertTriangle, Search, FileText, Table2,
  Plus, Trash2, Edit3, CheckCircle, XCircle, Eye, ChevronLeft, ChevronRight,
  Shield, Calendar, Clock, BookOpen, TrendingUp, Upload,
  ChevronDown, Settings, X, Trash, Send, Timer, Landmark,
  Loader2, ShieldAlert
} from 'lucide-react';
import { apiService } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { useLang } from '@/hooks/useLang';
import { toast } from 'sonner';

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTES LOCALSTORAGE
// ═══════════════════════════════════════════════════════════════════════════════
const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id';
const SELECTED_YEAR_LABEL_KEY = 'lybibal-selected-year-label';
const SELECTED_SCHOOL_ID_KEY = 'lybibal-selected-school-id';
const AUTH_STORAGE_KEY = 'auth-storage';
const SELECTED_YEAR_EVENT = 'lybibal-year-change';

const getStoredSchoolId = (): number | null => {
  try {
    const sid = localStorage.getItem(SELECTED_SCHOOL_ID_KEY);
    if (sid) return Number(sid);
    const authRaw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (authRaw) {
      const parsed = JSON.parse(authRaw);
      const state = parsed?.state || parsed;
      if (state?.user?.school_id) return Number(state.user.school_id);
    }
  } catch (e) { console.warn('getStoredSchoolId error:', e); }
  return null;
};
const getStoredYearId = (): number | null => {
  try {
    const yid = localStorage.getItem(SELECTED_YEAR_ID_KEY);
    if (yid) return Number(yid);
  } catch (e) { console.warn('getStoredYearId error:', e); }
  return null;
};
const getStoredYearLabel = (): string | null => {
  try { return localStorage.getItem(SELECTED_YEAR_LABEL_KEY); } catch (e) { return null; }
};

// ═══════════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════════
interface DisciplineRecord {
  id: number;
  student_id: number;
  student_name: string;
  student_matricule?: string;
  class_name?: string;
  class_id?: number;
  incident_type: string;
  record_type?: string;
  severity: string;
  description?: string;
  reason?: string;
  sanction?: string;
  date_recorded: string;
  duration_hours: number;
  duration_days: number;
  justified: boolean;
  justification_note?: string;
  sanction_executed: boolean;
  parent_notified: boolean;
  notes?: string;
  reported_by_name?: string;
  created_at?: string;
}

interface DisciplineStats {
  total_incidents: number;
  total_absences: number;
  total_absence_hours: number;
  total_absence_unjustified: number;
  total_absence_unjustified_hours: number;
  total_retards: number;
  total_exclusions: number;
  total_exclusion_days: number;
  total_consignes: number;
  total_consigne_hours: number;
  total_blames_conduite: number;
  total_blames_travail: number;
  pending_sanctions: number;
  parent_notifications: number;
  by_type: Record<string, number>;
  by_severity: Record<string, number>;
  by_month: Array<{month: string; count: number}>;
  top_students: Array<{student_id: number; student_name: string; count: number}>;
}

interface ClassItem {
  id: number;
  name: string;
  code?: string;
  level_name?: string;
}

interface Student {
  id: number;
  first_name: string;
  last_name: string;
  matricule: string;
  class_id?: number;
  class_name?: string;
  full_name?: string;
}

interface BulkEntry {
  student_id: number;
  duration_hours: number;
  duration_days: number;
  justified: boolean;
  reason: string;
  selected: boolean;
}

interface IncidentTypeConfig {
  key: string;
  label: string;
  color: string;
  icon: string;
  hasDuration: boolean;
}

interface SeverityConfig {
  key: string;
  label: string;
  color: string;
  points: number;
}

interface DeleteConfirmState {
  open: boolean;
  title: string;
  message: string;
  subMessage?: string;
  itemName?: string;
  confirmBtnText: string;
  onConfirm: () => void;
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════════════════════════
const SEVERITY_COLORS: Record<string, string> = {
  leger: 'bg-yellow-100 text-yellow-800',
  moyen: 'bg-orange-100 text-orange-800',
  grave: 'bg-red-100 text-red-800',
  critique: 'bg-red-200 text-red-900',
  tres_grave: 'bg-red-200 text-red-900',
};

const INCIDENT_LABELS: Record<string, string> = {
  absence: 'Absence',
  retard: 'Retard',
  exclusion: 'Exclusion',
  consigne: 'Consigne',
  blame: 'Blâme',
  trouble: 'Incident',
  autre: 'Autre',
  violence: 'Violence',
  fraude: 'Fraude',
};

const DEFAULT_INCIDENT_TYPES: IncidentTypeConfig[] = [
  { key: 'absence', label: 'Absence', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: 'Clock', hasDuration: true },
  { key: 'retard', label: 'Retard', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: 'Timer', hasDuration: false },
  { key: 'trouble', label: 'Trouble', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: 'AlertTriangle', hasDuration: false },
  { key: 'violence', label: 'Violence', color: 'bg-red-100 text-red-700 border-red-200', icon: 'ShieldAlert', hasDuration: true },
  { key: 'fraude', label: 'Fraude', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: 'FileText', hasDuration: false },
  { key: 'autre', label: 'Autre', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: 'AlertTriangle', hasDuration: false },
];

const DEFAULT_SEVERITY_LEVELS: SeverityConfig[] = [
  { key: 'leger', label: 'Léger', color: 'bg-green-100 text-green-700 border-green-200', points: 1 },
  { key: 'moyen', label: 'Moyen', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', points: 3 },
  { key: 'grave', label: 'Grave', color: 'bg-orange-100 text-orange-700 border-orange-200', points: 5 },
  { key: 'tres_grave', label: 'Très grave', color: 'bg-red-100 text-red-700 border-red-200', points: 10 },
];

const DEFAULT_SANCTIONS = [
  'Avertissement verbal',
  'Avertissement écrit',
  "Travail d'intérêt général",
  'Retenue',
  'Exclusion temporaire (1-3 jours)',
  'Exclusion temporaire (1-2 semaines)',
  'Exclusion définitive',
  'Convocation parents',
  'Rapport au conseil de discipline',
];

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Clock, Timer, AlertTriangle, ShieldAlert, FileText,
};

// ═══════════════════════════════════════════════════════════════════════════════
// SEARCHABLE SELECT
// ═══════════════════════════════════════════════════════════════════════════════
function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  disabled = false,
  required = false,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (val: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  disabled?: boolean;
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    return options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  }, [options, search]);

  const selected = options.find(o => o.value === value);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-left flex items-center justify-between text-sm ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'bg-white'}`}
      >
        <span className={selected ? 'text-gray-900' : 'text-gray-400'}>
          {selected?.label || placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div className="py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400">Aucun résultat</div>
            ) : (
              filtered.map(o => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => { onChange(o.value); setOpen(false); setSearch(''); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${value === o.value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                >
                  {o.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
      {required && <input type="hidden" value={value} required readOnly />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SOUS-COMPOSANTS
// ═══════════════════════════════════════════════════════════════════════════════
function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3">
      <div className="p-2 bg-gray-50 rounded-lg">{icon}</div>
      <div>
        <div className="text-lg font-bold text-gray-900">{value}</div>
        <div className="text-xs text-gray-500">{label}</div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex justify-between py-1 border-b border-gray-100 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900 text-right max-w-[60%]">{value}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE
// ═══════════════════════════════════════════════════════════════════════════════
export default function DisciplinePage() {
  const { user, hasPermission } = useAuthStore();
  const { lang } = useLang() as { lang: 'fr' | 'en' };

  // ─── PERMISSIONS PAR RÔLE ───
  const role = (user?.role || (user as any)?.role_name || '').toLowerCase();
  const isFullAccess = ['super_admin', 'admin', 'proviseur'].includes(role);
  const isLimited = ['censeur', 'surveillant_general', 'surveillant'].includes(role) || 
                      role.includes('surveillant') || role.includes('censeur');
  const canManage = isFullAccess || isLimited || hasPermission('discipline.manage') || hasPermission('discipline_write');

  useEffect(() => {
    console.log('[DisciplinePage] role=', role, 'isFullAccess=', isFullAccess, 'isLimited=', isLimited, 'canManage=', canManage);
  }, [role, isFullAccess, isLimited, canManage]);

  // ─── RÉCUPÉRATION LOCALSTORAGE ───
  const [schoolId, setSchoolId] = useState<number | null>(getStoredSchoolId);
  const [academicYearId, setAcademicYearId] = useState<number | null>(getStoredYearId);
  const [academicYearName, setAcademicYearName] = useState<string>(getStoredYearLabel() || '');

  const schoolIdRef = useRef<number | null>(schoolId);
  const yearIdRef = useRef<number | null>(academicYearId);
  useEffect(() => { schoolIdRef.current = schoolId; }, [schoolId]);
  useEffect(() => { yearIdRef.current = academicYearId; }, [academicYearId]);

  // ─── LISTES DYNAMIQUES ───
  const [incidentTypes, setIncidentTypes] = useState<IncidentTypeConfig[]>(() => {
    try {
      const stored = localStorage.getItem('lybibal-discipline-incident-types');
      return stored ? JSON.parse(stored) : DEFAULT_INCIDENT_TYPES;
    } catch { return DEFAULT_INCIDENT_TYPES; }
  });
  const [severityLevels, setSeverityLevels] = useState<SeverityConfig[]>(() => {
    try {
      const stored = localStorage.getItem('lybibal-discipline-severity-levels');
      return stored ? JSON.parse(stored) : DEFAULT_SEVERITY_LEVELS;
    } catch { return DEFAULT_SEVERITY_LEVELS; }
  });
  const [sanctionsList, setSanctionsList] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('lybibal-discipline-sanctions');
      return stored ? JSON.parse(stored) : DEFAULT_SANCTIONS;
    } catch { return DEFAULT_SANCTIONS; }
  });

  const persistIncidentTypes = (list: IncidentTypeConfig[]) => {
    setIncidentTypes(list);
    localStorage.setItem('lybibal-discipline-incident-types', JSON.stringify(list));
  };
  const persistSeverityLevels = (list: SeverityConfig[]) => {
    setSeverityLevels(list);
    localStorage.setItem('lybibal-discipline-severity-levels', JSON.stringify(list));
  };
  const persistSanctions = (list: string[]) => {
    setSanctionsList(list);
    localStorage.setItem('lybibal-discipline-sanctions', JSON.stringify(list));
  };

  // ─── ÉTATS DE CONFIGURATION ───
  const [showConfigType, setShowConfigType] = useState(false);
  const [showConfigSeverity, setShowConfigSeverity] = useState(false);
  const [showConfigSanction, setShowConfigSanction] = useState(false);
  const [newTypeLabel, setNewTypeLabel] = useState('');
  const [newTypeKey, setNewTypeKey] = useState('');
  const [newSeverityLabel, setNewSeverityLabel] = useState('');
  const [newSeverityKey, setNewSeverityKey] = useState('');
  const [newSeverityPoints, setNewSeverityPoints] = useState(1);
  const [newSanction, setNewSanction] = useState('');

  // ─── MODAL DE CONFIRMATION ───
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({
    open: false, title: '', message: '', confirmBtnText: '', onConfirm: () => {},
  });
  const openDeleteConfirm = (config: Omit<DeleteConfirmState, 'open'>) => {
    setDeleteConfirm({ ...config, open: true });
  };
  const closeDeleteConfirm = () => {
    setDeleteConfirm(prev => ({ ...prev, open: false }));
  };

  // ─── DONNÉES ───
  const [records, setRecords] = useState<DisciplineRecord[]>([]);
  const [stats, setStats] = useState<DisciplineStats | null>(null);
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(20);

  // Filtres
  const [filters, setFilters] = useState({
    student_id: '', incident_type: '', severity: '',
    date_from: '', date_to: '', sanction_executed: '', search: '', class_id: '',
  });
  // V11: debounce pour la recherche textuelle
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 400);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DisciplineRecord | null>(null);
  const [detailRecord, setDetailRecord] = useState<DisciplineRecord | null>(null);

  // Form
  const [formData, setFormData] = useState({
    student_id: '',
    incident_type: 'absence',
    severity: 'leger',
    reason: '',
    description: '',
    date_recorded: new Date().toISOString().slice(0, 16),
    duration_hours: 0,
    duration_days: 0,
    justified: false,
    sanction: '',
    notes: '',
  });

  // ─── ÉTATS POUR SÉLECTION CLASSE → ÉLÈVE ───
  const [incidentClassId, setIncidentClassId] = useState('');
  const [incidentStudents, setIncidentStudents] = useState<Student[]>([]);
  const [incidentStudentsLoading, setIncidentStudentsLoading] = useState(false);

  // Bulk
  const [bulkClassId, setBulkClassId] = useState<number | ''>('');
  const [bulkDate, setBulkDate] = useState(new Date().toISOString().slice(0, 16));
  const [bulkEntries, setBulkEntries] = useState<BulkEntry[]>([]);
  const [studentsInClass, setStudentsInClass] = useState<Student[]>([]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // TRADUCTIONS
  // ═══════════════════════════════════════════════════════════════════════════════
  const $t = {
    fr: {
      title: 'Gestion Disciplinaire',
      subtitle: 'Suivi des absences, retards, sanctions et convocations',
      school: 'Établissement', academicYear: 'Année scolaire',
      noYear: 'Aucune année sélectionnée', noSchool: 'Aucun établissement sélectionné',
      loading: 'Chargement...', error: 'Erreur lors du chargement',
      totalAbsences: 'ABS. TOTALES', totalAbsencesUnjustified: 'ABS. NON JUST.',
      consignes: 'CONSIGNES', exclusions: 'EXCLUSIONS',
      totalIncidents: 'Total incidents', pendingSanctions: 'Sanctions en attente',
      parentsNotified: 'Parents notifiés', toTreat: 'À traiter',
      filters: 'Filtres', newIncident: 'Nouvel incident', quickEntry: 'Saisie rapide classe',
      all: 'Tous', allTypes: 'Tous les types', allSeverities: 'Toutes les sévérités',
      allClasses: 'Toutes les classes',
      executed: 'Exécutée', pending: 'En attente',
      dateStart: 'Date début', dateEnd: 'Date fin', search: 'Recherche',
      student: 'Élève', type: 'Type', severity: 'Sévérité',
      description: 'Description', reason: 'Motif', sanction: 'Sanction',
      status: 'Statut', actions: 'Actions', noRecords: 'Aucun incident trouvé',
      reset: 'Réinitialiser', apply: 'Appliquer', cancel: 'Annuler',
      save: 'Enregistrer', update: 'Mettre à jour',
      deleteConfirmTitle: "Supprimer l'incident",
      deleteConfirmDesc: "Cette action est irréversible. L'incident de",
      deleteConfirmDate: 'en date du',
      deleteConfirmWarn: 'sera définitivement supprimé.',
      deleteConfirmBtn: 'Supprimer définitivement',
      deleteTypeTitle: 'Supprimer le type',
      deleteTypeDesc: "Le type d'incident",
      deleteTypeWarn: 'sera supprimé de la liste. Les incidents existants ne seront pas affectés.',
      deleteSeverityTitle: 'Supprimer la sévérité',
      deleteSeverityDesc: 'Le niveau de sévérité',
      deleteSeverityWarn: 'sera supprimé de la liste.',
      deleteSanctionTitle: 'Supprimer la sanction',
      deleteSanctionDesc: 'La sanction',
      deleteSanctionWarn: 'sera supprimée de la liste.',
      deleted: 'Incident supprimé',
      updated: 'Statut mis à jour', convocationSent: 'Convocation envoyée',
      convocationError: "Erreur lors de l'envoi", incidentSaved: 'Incident enregistré',
      incidentUpdated: 'Incident mis à jour', saveError: "Erreur lors de l'enregistrement",
      deleteError: "Erreur lors de la suppression", convocationTitle: 'Convocation parent',
      convocationWarning: 'Une convocation sera envoyée aux parents de',
      convocationAbout: 'concernant cet incident.', send: 'Envoyer',
      details: 'Détails', edit: 'Modifier', markExecuted: 'Marquer exécutée',
      markPending: 'Marquer non exécutée', convocation: 'Convocation parent',
      delete: 'Supprimer', page: 'Page', of: 'sur', showing: 'Affichage de',
      to: 'à', entries: 'entrées trouvées', previous: 'Précédent', next: 'Suivant',
      hours: 'Heure(s)', days: 'Jour(s)', duration: 'Durée', justified: 'Justifiée',
      justify: 'Justifier', unjustify: 'Annuler justification',
      classSelect: 'Classe', selectClass: 'Sélectionner une classe',
      selectStudent: 'Sélectionner un élève',
      loadStudents: 'Charger les élèves', allPresent: 'Tous présents',
      allAbsent: 'Tous absents (1h)', bulkSaved: 'Saisie rapide enregistrée',
      bulkError: 'Erreur saisie rapide', noClassSelected: 'Veuillez sélectionner une classe',
      add: 'Ajouter', remove: 'Supprimer', manageLists: 'Gérer les listes',
      newType: 'Nouveau type', newSeverity: 'Nouvelle sévérité', newSanction: 'Nouvelle sanction',
      label: 'Libellé', key: 'Code', points: 'Points',
      searchClass: 'Rechercher une classe...',
      searchStudent: 'Rechercher un élève...',
      downloadPDF: 'PDF', downloadExcel: 'Excel',
      downloadList: 'Télécharger liste', downloadStats: 'Télécharger stats',
      byType: 'Par type', bySeverity: 'Par sévérité', byMonth: 'Par mois',
      topStudents: 'Top élèves', statsDetails: 'Statistiques détaillées',
      close: 'Fermer', incidentDetails: "Détails de l'incident",
      reportedBy: 'Signalé par', createdAt: 'Créé le',
      justificationNote: 'Note de justification', yes: 'Oui', no: 'Non',
      back: 'Retour', configTypes: 'Configurer types',
      configSeverities: 'Configurer sévérités', configSanctions: 'Configurer sanctions',
    },
    en: {
      title: 'Discipline Management',
      subtitle: 'Absences, delays, sanctions and summons tracking',
      school: 'School', academicYear: 'Academic Year',
      noYear: 'No year selected', noSchool: 'No school selected',
      loading: 'Loading...', error: 'Loading error',
      totalAbsences: 'TOTAL ABS.', totalAbsencesUnjustified: 'UNJUST. ABS.',
      consignes: 'DETENTIONS', exclusions: 'EXCLUSIONS',
      totalIncidents: 'Total incidents', pendingSanctions: 'Pending sanctions',
      parentsNotified: 'Parents notified', toTreat: 'To treat',
      filters: 'Filters', newIncident: 'New incident', quickEntry: 'Quick class entry',
      all: 'All', allTypes: 'All types', allSeverities: 'All severities',
      allClasses: 'All classes',
      executed: 'Executed', pending: 'Pending',
      dateStart: 'Start date', dateEnd: 'End date', search: 'Search',
      student: 'Student', type: 'Type', severity: 'Severity',
      description: 'Description', reason: 'Reason', sanction: 'Sanction',
      status: 'Status', actions: 'Actions', noRecords: 'No incidents found',
      reset: 'Reset', apply: 'Apply', cancel: 'Cancel',
      save: 'Save', update: 'Update',
      deleteConfirmTitle: 'Delete incident',
      deleteConfirmDesc: 'This action is irreversible. The incident of',
      deleteConfirmDate: 'dated',
      deleteConfirmWarn: 'will be permanently deleted.',
      deleteConfirmBtn: 'Delete permanently',
      deleteTypeTitle: 'Delete type',
      deleteTypeDesc: 'The incident type',
      deleteTypeWarn: 'will be removed from the list. Existing incidents will not be affected.',
      deleteSeverityTitle: 'Delete severity',
      deleteSeverityDesc: 'The severity level',
      deleteSeverityWarn: 'will be removed from the list.',
      deleteSanctionTitle: 'Delete sanction',
      deleteSanctionDesc: 'The sanction',
      deleteSanctionWarn: 'will be removed from the list.',
      deleted: 'Incident deleted',
      updated: 'Status updated', convocationSent: 'Summons sent',
      convocationError: 'Error sending summons', incidentSaved: 'Incident saved',
      incidentUpdated: 'Incident updated', saveError: 'Error saving',
      deleteError: 'Error deleting', convocationTitle: 'Parent summons',
      convocationWarning: 'A summons will be sent to the parents of',
      convocationAbout: 'regarding this incident.', send: 'Send',
      details: 'Details', edit: 'Edit', markExecuted: 'Mark executed',
      markPending: 'Mark pending', convocation: 'Parent summons',
      delete: 'Delete', page: 'Page', of: 'of', showing: 'Showing',
      to: 'to', entries: 'entries found', previous: 'Previous', next: 'Next',
      hours: 'Hour(s)', days: 'Day(s)', duration: 'Duration', justified: 'Justified',
      justify: 'Justify', unjustify: 'Remove justification',
      classSelect: 'Class', selectClass: 'Select a class',
      selectStudent: 'Select a student',
      loadStudents: 'Load students', allPresent: 'All present',
      allAbsent: 'All absent (1h)', bulkSaved: 'Quick entry saved',
      bulkError: 'Quick entry error', noClassSelected: 'Please select a class',
      add: 'Add', remove: 'Remove', manageLists: 'Manage lists',
      newType: 'New type', newSeverity: 'New severity', newSanction: 'New sanction',
      label: 'Label', key: 'Code', points: 'Points',
      searchClass: 'Search a class...',
      searchStudent: 'Search a student...',
      downloadPDF: 'PDF', downloadExcel: 'Excel',
      downloadList: 'Download list', downloadStats: 'Download stats',
      byType: 'By type', bySeverity: 'By severity', byMonth: 'By month',
      topStudents: 'Top students', statsDetails: 'Detailed statistics',
      close: 'Close', incidentDetails: 'Incident details',
      reportedBy: 'Reported by', createdAt: 'Created at',
      justificationNote: 'Justification note', yes: 'Yes', no: 'No',
      back: 'Back', configTypes: 'Configure types',
      configSeverities: 'Configure severities', configSanctions: 'Configure sanctions',
    }
  }[lang];

  // ═══════════════════════════════════════════════════════════════════════════════
  // CHARGEMENT
  // ═══════════════════════════════════════════════════════════════════════════════
  const loadClasses = useCallback(async () => {
    let classesLoaded = false;

    // Essai 1: /discipline/allowed-classes
    try {
      const res: any = await apiService.get('/discipline/allowed-classes');
      const raw = Array.isArray(res) ? res : (Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.data || []));
      if (Array.isArray(raw) && raw.length > 0) {
        setClasses(raw);
        console.log('[DisciplinePage] Classes from /allowed-classes:', raw.length);
        classesLoaded = true;
        return;
      }
    } catch (e: any) {
      console.warn('[DisciplinePage] /allowed-classes failed:', e.message);
    }

    // Essai 2: /classes avec school_id
    try {
      const params: Record<string, any> = { per_page: 1000 };
      if (schoolIdRef.current) params.school_id = schoolIdRef.current;
      const res2: any = await apiService.get('/classes', { params });
      const raw2 = Array.isArray(res2) ? res2 : (Array.isArray(res2.data) ? res2.data : (res2.data?.items || res2.data?.data || []));
      if (Array.isArray(raw2) && raw2.length > 0) {
        setClasses(raw2);
        console.log('[DisciplinePage] Classes from /classes:', raw2.length);
        classesLoaded = true;
        return;
      }
      console.warn('[DisciplinePage] /classes with school_id returned empty');
    } catch (e2: any) {
      console.warn('[DisciplinePage] /classes with school_id failed:', e2.message);
    }

    // Essai 3: /classes SANS school_id (toujours, pas seulement admin)
    try {
      const res3: any = await apiService.get('/classes', { params: { per_page: 1000 } });
      const raw3 = Array.isArray(res3) ? res3 : (Array.isArray(res3.data) ? res3.data : (res3.data?.items || res3.data?.data || []));
      if (Array.isArray(raw3) && raw3.length > 0) {
        setClasses(raw3);
        console.log('[DisciplinePage] Classes from /classes (no filter):', raw3.length);
        classesLoaded = true;
        return;
      }
      console.warn('[DisciplinePage] /classes without filter returned empty');
    } catch (e3: any) {
      console.warn('[DisciplinePage] /classes without filter failed:', e3.message);
    }

    if (!classesLoaded) {
      setClasses([]);
      console.error('[DisciplinePage] All class loading attempts failed');
    }
  }, []);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, any> = {
        page, per_page: perPage,
      };
      if (schoolIdRef.current) params.school_id = schoolIdRef.current;
      if (yearIdRef.current) params.academic_year_id = yearIdRef.current;
      if (filters.class_id) params.class_id = Number(filters.class_id);
      if (filters.student_id) params.student_id = Number(filters.student_id);
      if (filters.incident_type) params.incident_type = filters.incident_type;
      if (filters.severity) params.severity = filters.severity;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.sanction_executed) params.sanction_executed = filters.sanction_executed === 'true';
      if (debouncedSearch) params.search = debouncedSearch;

      const res: any = await apiService.get('/discipline', { params });
      const data = res.items !== undefined ? res : (res.data || {});
      if (data.items) {
        setRecords(data.items);
        setTotalPages(data.pages || 1);
      }
    } catch (e: any) {
      setError('Erreur chargement: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [page, perPage, debouncedSearch, filters.class_id, filters.incident_type, filters.severity, filters.date_from, filters.date_to, filters.sanction_executed, filters.student_id]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const params: Record<string, any> = {};
      if (schoolIdRef.current) params.school_id = schoolIdRef.current;
      if (yearIdRef.current) params.academic_year_id = yearIdRef.current;
      if (filters.class_id) params.class_id = Number(filters.class_id);
      const res: any = await apiService.get('/discipline/stats', { params });
      const data = res.total_incidents !== undefined ? res : (res.data || null);
      if (data) {
        setStats(data);
      }
    } catch (e: any) {
      console.error('Stats error:', e);
    } finally {
      setStatsLoading(false);
    }
  }, [filters.class_id]);

  const loadIncidentStudents = useCallback(async (classId: string) => {
    if (!classId) { setIncidentStudents([]); return; }
    setIncidentStudentsLoading(true);
    try {
      const params: Record<string, any> = { per_page: 1000, class_id: Number(classId) };
      if (schoolIdRef.current) params.school_id = schoolIdRef.current;
      // Si pas de résultat avec school_id, essayer sans (fallback admin)
      let res: any;
      try {
        res = await apiService.get('/students', { params });
      } catch (e: any) {
        if (schoolIdRef.current && isFullAccess) {
          const { school_id, ...paramsNoSchool } = params;
          res = await apiService.get('/students', { params: paramsNoSchool });
        } else {
          throw e;
        }
      }
      const data = res.items !== undefined ? res : (res.data || {});
      const items: Student[] = data.items || data.data?.items || data.data || [];
      setIncidentStudents(items);
      if (items.length === 0) {
        console.warn('[DisciplinePage] Aucun élève trouvé pour la classe', classId);
      }
    } catch (e: any) {
      console.error('Error loading incident students:', e);
      toast.error(lang === 'fr' ? 'Erreur chargement élèves: ' + (e.response?.data?.detail || e.message) : 'Error loading students: ' + (e.response?.data?.detail || e.message));
      setIncidentStudents([]);
    } finally {
      setIncidentStudentsLoading(false);
    }
  }, [isFullAccess, lang]);

  // V11: Charger classes quand schoolId change, records/stats quand filtres/page changent
  useEffect(() => {
    loadClasses();
  }, [schoolId, loadClasses]);

  useEffect(() => {
    if (schoolIdRef.current) {
      loadRecords();
      loadStats();
    }
  }, [loadRecords, loadStats]);

  // ─── ÉCOUTE CHANGEMENTS ANNÉE ───
  useEffect(() => {
    const handleYearChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail?.year_id) return;
      const newYearId = Number(detail.year_id);
      const newSchoolId = detail?.school_id !== undefined ? Number(detail.school_id) : null;
      let changed = false;
      if (newYearId && newYearId !== yearIdRef.current) {
        setAcademicYearId(newYearId);
        yearIdRef.current = newYearId;
        setAcademicYearName(detail.label || getStoredYearLabel() || '');
        changed = true;
      }
      if (newSchoolId !== null && !isNaN(newSchoolId) && newSchoolId !== schoolIdRef.current) {
        setSchoolId(newSchoolId);
        schoolIdRef.current = newSchoolId;
        changed = true;
      }
      if (changed) {
        toast.info(lang === 'fr' ? 'Contexte scolaire mis à jour — rechargement' : 'School context updated — reloading');
      }
    };
    window.addEventListener(SELECTED_YEAR_EVENT, handleYearChange);
    const handleStorageChange = (e: StorageEvent) => {
      let changed = false;
      if (e.key === SELECTED_YEAR_ID_KEY) {
        const newYearId = e.newValue ? Number(e.newValue) : null;
        if (newYearId && newYearId !== yearIdRef.current) {
          setAcademicYearId(newYearId); yearIdRef.current = newYearId;
          setAcademicYearName(localStorage.getItem(SELECTED_YEAR_LABEL_KEY) || '');
          changed = true;
        }
      }
      if (e.key === SELECTED_SCHOOL_ID_KEY) {
        const newSchoolId = e.newValue ? Number(e.newValue) : null;
        if (newSchoolId && newSchoolId !== schoolIdRef.current) {
          setSchoolId(newSchoolId); schoolIdRef.current = newSchoolId;
          changed = true;
        }
      }
      if (changed) {
        // loadRecords et loadStats seront déclenchés par le changement de schoolId
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener(SELECTED_YEAR_EVENT, handleYearChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [lang]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // TÉLÉCHARGEMENTS (fetch natif pour éviter CORS/IDM)
  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ V2 : téléchargement via apiService (axios + cookies httpOnly).
  // L'ancienne version lisait un token dans localStorage (vide désormais) et
  // ouvrait window.open → "Token manquant" sans aucune requête réseau.
  const handleDownloadList = async (format: 'pdf' | 'excel') => {
    try {
      const params: Record<string, any> = { format };
      if (schoolIdRef.current) params.school_id = schoolIdRef.current;
      if (yearIdRef.current) params.academic_year_id = yearIdRef.current;
      if (filters.class_id) params.class_id = filters.class_id;
      if (filters.incident_type) params.incident_type = filters.incident_type;
      if (filters.severity) params.severity = filters.severity;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (debouncedSearch) params.search = debouncedSearch;

      const res: any = await apiService.get('/discipline/download/list', {
        params,
        responseType: 'blob' as any,
      });
      const blob = res instanceof Blob ? res : new Blob([res]);
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `discipline_liste_${new Date().toISOString().split('T')[0]}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(lang === 'fr' ? 'Téléchargement terminé' : 'Download complete');
    } catch (err) {
      console.error('[DISCIPLINE_DOWNLOAD_LIST] Erreur:', err);
      toast.error(lang === 'fr' ? 'Erreur de téléchargement — reconnectez-vous' : 'Download error — sign in again');
    }
  };

  const handleDownloadStats = async (format: 'pdf' | 'excel') => {
    try {
      const params: Record<string, any> = { format };
      if (schoolIdRef.current) params.school_id = schoolIdRef.current;
      if (yearIdRef.current) params.academic_year_id = yearIdRef.current;
      if (filters.class_id) params.class_id = filters.class_id;

      const res: any = await apiService.get('/discipline/download/stats', {
        params,
        responseType: 'blob' as any,
      });
      const blob = res instanceof Blob ? res : new Blob([res]);
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `discipline_stats_${new Date().toISOString().split('T')[0]}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(lang === 'fr' ? 'Téléchargement terminé' : 'Download complete');
    } catch (err) {
      console.error('[DISCIPLINE_DOWNLOAD_STATS] Erreur:', err);
      toast.error(lang === 'fr' ? 'Erreur de téléchargement — reconnectez-vous' : 'Download error — sign in again');
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // CRUD
  // ═══════════════════════════════════════════════════════════════════════════════
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.student_id || !formData.incident_type) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (!schoolIdRef.current && !isFullAccess) {
      toast.error($t.noSchool);
      return;
    }
    setLoading(true);
    try {
      const payload: Record<string, any> = {
        ...formData,
        student_id: parseInt(formData.student_id),
        duration_hours: parseInt(formData.duration_hours as any) || 0,
        duration_days: parseInt(formData.duration_days as any) || 0,
        school_id: schoolIdRef.current || null,
        academic_year_id: yearIdRef.current || null,
      };
      if (editingRecord) {
        delete payload.school_id;
        delete payload.academic_year_id;
        await apiService.put(`/discipline/${editingRecord.id}`, payload);
        setSuccessMsg($t.incidentUpdated);
        toast.success($t.incidentUpdated);
      } else {
        await apiService.post('/discipline', payload);
        setSuccessMsg($t.incidentSaved);
        toast.success($t.incidentSaved);
      }
      setShowModal(false);
      setEditingRecord(null);
      resetForm();
      loadRecords();
      loadStats();
    } catch (e: any) {
      const msg = e.response?.data?.detail || e.message;
      setError('Erreur: ' + msg);
      toast.error(msg || $t.saveError);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    openDeleteConfirm({
      title: $t.deleteConfirmTitle,
      message: `${$t.deleteConfirmDesc} ${$t.deleteConfirmWarn}`,
      confirmBtnText: $t.deleteConfirmBtn,
      onConfirm: async () => {
        try {
          await apiService.delete(`/discipline/${id}`);
          setSuccessMsg($t.deleted);
          toast.success($t.deleted);
          closeDeleteConfirm();
          loadRecords();
          loadStats();
        } catch (e: any) {
          setError('Erreur suppression: ' + (e.response?.data?.detail || e.message));
          toast.error($t.deleteError);
        }
      },
    });
  };

  const handleToggleSanction = async (record: DisciplineRecord) => {
    try {
      await apiService.put(`/discipline/${record.id}`, { sanction_executed: !record.sanction_executed });
      toast.success($t.updated);
      loadRecords();
    } catch (e: any) {
      toast.error('Erreur');
    }
  };

  const handleJustify = async (id: number) => {
    try {
      await apiService.put(`/discipline/${id}/justify`, { justification_note: 'Justifié via interface' });
      setSuccessMsg(lang === 'fr' ? 'Absence justifiée' : 'Absence justified');
      toast.success(lang === 'fr' ? 'Absence justifiée' : 'Absence justified');
      loadRecords();
      loadStats();
    } catch (e: any) {
      setError('Erreur: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleConvocation = async (id: number) => {
    try {
      await apiService.post(`/discipline/${id}/convocation`);
      setSuccessMsg($t.convocationSent);
      toast.success($t.convocationSent);
      loadRecords();
    } catch (e: any) {
      setError('Erreur: ' + (e.response?.data?.detail || e.message));
      toast.error($t.convocationError);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // GESTION LISTES DYNAMIQUES
  // ═══════════════════════════════════════════════════════════════════════════════
  const addIncidentType = () => {
    if (!newTypeKey.trim() || !newTypeLabel.trim()) return;
    const key = newTypeKey.trim().toLowerCase().replace(/\s+/g, '_');
    if (incidentTypes.find(t => t.key === key)) { toast.error('Code déjà utilisé'); return; }
    const newList = [...incidentTypes, { key, label: newTypeLabel.trim(), color: 'bg-gray-100 text-gray-700 border-gray-200', icon: 'AlertTriangle', hasDuration: false }];
    persistIncidentTypes(newList);
    setNewTypeKey(''); setNewTypeLabel(''); setShowConfigType(false);
    toast.success(lang === 'fr' ? 'Type ajouté' : 'Type added');
  };
  const removeIncidentType = (key: string) => {
    const type = incidentTypes.find(t => t.key === key);
    openDeleteConfirm({
      title: $t.deleteTypeTitle,
      message: `${$t.deleteTypeDesc} "${type?.label || key}"`,
      subMessage: $t.deleteTypeWarn,
      confirmBtnText: lang === 'fr' ? 'Supprimer' : 'Delete',
      onConfirm: () => {
        persistIncidentTypes(incidentTypes.filter(t => t.key !== key));
        if (formData.incident_type === key) setFormData(f => ({ ...f, incident_type: incidentTypes[0]?.key || 'absence' }));
        closeDeleteConfirm();
      },
    });
  };

  const addSeverity = () => {
    if (!newSeverityKey.trim() || !newSeverityLabel.trim()) return;
    const key = newSeverityKey.trim().toLowerCase().replace(/\s+/g, '_');
    if (severityLevels.find(s => s.key === key)) { toast.error('Code déjà utilisé'); return; }
    const newList = [...severityLevels, { key, label: newSeverityLabel.trim(), color: 'bg-gray-100 text-gray-700 border-gray-200', points: newSeverityPoints }];
    persistSeverityLevels(newList);
    setNewSeverityKey(''); setNewSeverityLabel(''); setNewSeverityPoints(1); setShowConfigSeverity(false);
    toast.success(lang === 'fr' ? 'Sévérité ajoutée' : 'Severity added');
  };
  const removeSeverity = (key: string) => {
    const sev = severityLevels.find(s => s.key === key);
    openDeleteConfirm({
      title: $t.deleteSeverityTitle,
      message: `${$t.deleteSeverityDesc} "${sev?.label || key}"`,
      subMessage: $t.deleteSeverityWarn,
      confirmBtnText: lang === 'fr' ? 'Supprimer' : 'Delete',
      onConfirm: () => {
        persistSeverityLevels(severityLevels.filter(s => s.key !== key));
        if (formData.severity === key) setFormData(f => ({ ...f, severity: severityLevels[0]?.key || 'leger' }));
        closeDeleteConfirm();
      },
    });
  };

  const addSanction = () => {
    if (!newSanction.trim()) return;
    if (sanctionsList.includes(newSanction.trim())) { toast.error('Sanction déjà existante'); return; }
    const newList = [...sanctionsList, newSanction.trim()];
    persistSanctions(newList);
    setNewSanction(''); setShowConfigSanction(false);
    toast.success(lang === 'fr' ? 'Sanction ajoutée' : 'Sanction added');
  };
  const removeSanction = (idx: number) => {
    const sanction = sanctionsList[idx];
    openDeleteConfirm({
      title: $t.deleteSanctionTitle,
      message: `${$t.deleteSanctionDesc} "${sanction}"`,
      subMessage: $t.deleteSanctionWarn,
      confirmBtnText: lang === 'fr' ? 'Supprimer' : 'Delete',
      onConfirm: () => {
        persistSanctions(sanctionsList.filter((_, i) => i !== idx));
        closeDeleteConfirm();
      },
    });
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // BULK ABSENCES
  // ═══════════════════════════════════════════════════════════════════════════════
  const loadStudentsForBulk = async (classId: number) => {
    if (!classId) return;
    try {
      const params: Record<string, any> = { class_id: classId, per_page: 1000 };
      if (schoolIdRef.current) params.school_id = schoolIdRef.current;
      const res: any = await apiService.get('/students', { params });
      const data = res.items !== undefined ? res : (res.data || {});
      const items: Student[] = data.items || data.data?.items || data.data || [];
      setStudentsInClass(items);
      setBulkEntries(items.map((s: any) => ({
        student_id: s.id,
        duration_hours: 2,
        duration_days: 0,
        justified: false,
        reason: 'Absence',
        selected: false,
      })));
    } catch (e) {
      console.error('Load students error:', e);
      toast.error(lang === 'fr' ? 'Erreur chargement élèves' : 'Error loading students');
      setStudentsInClass([]);
      setBulkEntries([]);
    }
  };

  const handleBulkSave = async () => {
    if (!bulkClassId) {
      setError($t.noClassSelected);
      return;
    }
    const activeEntries = bulkEntries.filter(e => e.selected && (e.duration_hours > 0 || e.duration_days > 0));
    if (activeEntries.length === 0) {
      toast.error(lang === 'fr' ? 'Aucun élève sélectionné avec durée > 0' : 'No student selected with duration > 0');
      return;
    }
    setLoading(true);
    try {
      await apiService.post('/discipline/bulk-absences', {
        class_id: bulkClassId,
        date_recorded: new Date(bulkDate).toISOString(),
        school_id: schoolIdRef.current || null,
        academic_year_id: yearIdRef.current || null,
        entries: activeEntries.map(e => ({
          student_id: e.student_id,
          duration_hours: e.duration_hours,
          duration_days: e.duration_days,
          justified: e.justified,
          reason: e.reason,
        })),
      });
      setSuccessMsg($t.bulkSaved);
      toast.success($t.bulkSaved);
      setShowBulkModal(false);
      loadRecords();
      loadStats();
    } catch (e: any) {
      setError('Erreur bulk: ' + (e.response?.data?.detail || e.message));
      toast.error($t.bulkError);
    } finally {
      setLoading(false);
    }
  };

  const setAllBulk = (hours: number, days: number) => {
    setBulkEntries(prev => prev.map(e => ({
      ...e, selected: hours > 0 || days > 0, duration_hours: hours, duration_days: days,
    })));
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // UTILITAIRES
  // ═══════════════════════════════════════════════════════════════════════════════
  const resetForm = () => {
    setFormData({
      student_id: '', incident_type: incidentTypes[0]?.key || 'absence',
      severity: severityLevels[0]?.key || 'leger',
      reason: '', description: '',
      date_recorded: new Date().toISOString().slice(0, 16),
      sanction: '', notes: '', duration_hours: 0, duration_days: 0, justified: false,
    });
    setIncidentClassId('');
    setIncidentStudents([]);
  };

  const editRecord = (record: DisciplineRecord) => {
    setEditingRecord(record);
    const classId = record.class_id;
    if (classId) {
      setIncidentClassId(classId.toString());
      loadIncidentStudents(classId.toString());
    } else {
      const student = studentsInClass.find(s => s.id === record.student_id);
      if (student?.class_id) {
        setIncidentClassId(student.class_id.toString());
        loadIncidentStudents(student.class_id.toString());
      }
    }
    setFormData({
      student_id: record.student_id.toString(),
      incident_type: record.incident_type || record.record_type || incidentTypes[0]?.key || 'absence',
      severity: record.severity || severityLevels[0]?.key || 'leger',
      description: record.description || '', reason: record.reason || '',
      date_recorded: record.date_recorded ? record.date_recorded.slice(0, 16) : new Date().toISOString().slice(0, 16),
      sanction: record.sanction || '', notes: record.notes || '',
      duration_hours: record.duration_hours || 0, duration_days: record.duration_days || 0,
      justified: record.justified || false,
    });
    setShowModal(true);
  };

  const getIncidentBadge = (type: string) => {
    const config = incidentTypes.find(t => t.key === type) || incidentTypes[incidentTypes.length - 1];
    const IconComp = ICON_MAP[config?.icon || 'AlertTriangle'] || AlertTriangle;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${config?.color || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
        <IconComp className="w-3 h-3" />
        {config?.label || INCIDENT_LABELS[type] || type}
      </span>
    );
  };

  const getSeverityBadge = (severity: string) => {
    const config = severityLevels.find(s => s.key === severity);
    if (config) {
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
          {config.label} ({config.points} pts)
        </span>
      );
    }
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_COLORS[severity] || 'bg-gray-100 text-gray-700'}`}>
        {severity}
      </span>
    );
  };

  const clearFilters = () => {
    setFilters({ student_id: '', incident_type: '', severity: '', date_from: '', date_to: '', sanction_executed: '', search: '', class_id: '' });
    setPage(1);
  };

  const hasDuration = incidentTypes.find(t => t.key === formData.incident_type)?.hasDuration || false;

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDU
  // ═══════════════════════════════════════════════════════════════════════════════
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-7 h-7 text-red-600" />
            {$t.title}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{$t.subtitle}</p>
          <div className="flex items-center gap-2 mt-1.5">
            {schoolId && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full flex items-center gap-1">
                <Landmark className="w-3 h-3" /> {$t.school} ID: {schoolId}
              </span>
            )}
            {academicYearId && academicYearName && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {academicYearName}
              </span>
            )}
            {!schoolId && (
              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> {$t.noSchool}
              </span>
            )}
            {schoolId && !academicYearId && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> {$t.noYear}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {canManage && (
            <>
              <button
                onClick={() => { setShowBulkModal(true); setBulkClassId(''); setStudentsInClass([]); }}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
              >
                <Upload className="w-4 h-4" />
                {$t.quickEntry}
              </button>
              <button
                onClick={() => { setEditingRecord(null); resetForm(); setShowModal(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                <Plus className="w-4 h-4" />
                {$t.newIncident}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertTriangle className="w-5 h-5" />
          {error}
          <button onClick={() => setError('')} className="ml-auto text-sm underline">Fermer</button>
        </div>
      )}
      {successMsg && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <CheckCircle className="w-5 h-5" />
          {successMsg}
        </div>
      )}

      {/* Stats Cards */}
      {statsLoading ? (
        <div className="flex items-center justify-center py-8 mb-6">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400 mr-2" />
          <span className="text-gray-500">{$t.loading}</span>
        </div>
      ) : stats ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          <StatCard icon={<AlertTriangle className="w-5 h-5 text-red-600" />} label={$t.totalIncidents} value={stats.total_incidents} />
          <StatCard icon={<Clock className="w-5 h-5 text-orange-600" />} label={$t.totalAbsences} value={stats.total_absences} />
          <StatCard icon={<Clock className="w-5 h-5 text-yellow-600" />} label={$t.totalAbsencesUnjustified} value={stats.total_absence_unjustified} />
          <StatCard icon={<Shield className="w-5 h-5 text-purple-600" />} label={$t.exclusions} value={stats.total_exclusions} />
          <StatCard icon={<BookOpen className="w-5 h-5 text-blue-600" />} label={$t.consignes} value={stats.total_consignes} />
          <StatCard icon={<TrendingUp className="w-5 h-5 text-green-600" />} label={$t.toTreat} value={stats.pending_sanctions} />
        </div>
      ) : null}

      {/* Filtres + Téléchargements */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          {/* Filtre Classe */}
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">{$t.classSelect}</label>
            <select
              value={filters.class_id}
              onChange={(e) => { setFilters({...filters, class_id: e.target.value}); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{$t.allClasses}</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>{c.name} {c.level_name ? `(${c.level_name})` : ''}</option>
              ))}
            </select>
          </div>

          {/* Filtre Type */}
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">{$t.type}</label>
            <select
              value={filters.incident_type}
              onChange={(e) => { setFilters({...filters, incident_type: e.target.value}); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">{$t.allTypes}</option>
              {incidentTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>

          {/* Filtre Sévérité */}
          <div className="w-32">
            <label className="block text-xs font-medium text-gray-600 mb-1">{$t.severity}</label>
            <select
              value={filters.severity}
              onChange={(e) => { setFilters({...filters, severity: e.target.value}); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">{$t.allSeverities}</option>
              {severityLevels.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>

          {/* Dates */}
          <div className="w-36">
            <label className="block text-xs font-medium text-gray-600 mb-1">{$t.dateStart}</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => { setFilters({...filters, date_from: e.target.value}); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div className="w-36">
            <label className="block text-xs font-medium text-gray-600 mb-1">{$t.dateEnd}</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => { setFilters({...filters, date_to: e.target.value}); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          {/* Recherche */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">{$t.search}</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={lang === 'fr' ? 'Nom, matricule...' : 'Name, ID...'}
                value={filters.search}
                onChange={(e) => { setFilters({...filters, search: e.target.value}); setPage(1); }}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>

          {/* Reset */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1 opacity-0">Reset</label>
            <button
              onClick={clearFilters}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm text-gray-600"
              title={$t.reset}
            >
              {$t.reset}
            </button>
          </div>

          {/* Boutons téléchargement LISTE */}
          <div className="flex gap-1">
            <button
              onClick={() => handleDownloadList('pdf')}
              className="flex items-center gap-1 px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 text-sm border border-red-200"
              title={$t.downloadList + ' PDF'}
            >
              <FileText className="w-4 h-4" />
              {$t.downloadPDF}
            </button>
            <button
              onClick={() => handleDownloadList('excel')}
              className="flex items-center gap-1 px-3 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-sm border border-green-200"
              title={$t.downloadList + ' Excel'}
            >
              <Table2 className="w-4 h-4" />
              {$t.downloadExcel}
            </button>
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{$t.student}</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{$t.classSelect}</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{$t.type}</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{$t.severity}</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{$t.dateStart}</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{$t.reason}</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{$t.sanction}</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">{$t.justified}</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">{$t.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> {$t.loading}
                </td></tr>
              ) : records.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">{$t.noRecords}</td></tr>
              ) : (
                records.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{r.student_name}</div>
                      <div className="text-xs text-gray-500">{r.student_matricule}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{r.class_name || '—'}</td>
                    <td className="px-4 py-3">{getIncidentBadge(r.incident_type || r.record_type || 'autre')}</td>
                    <td className="px-4 py-3">{getSeverityBadge(r.severity)}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {r.date_recorded ? new Date(r.date_recorded).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US') : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-[150px] truncate">{r.reason || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-[150px] truncate">{r.sanction || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      {r.justified ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400 mx-auto" />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => { setDetailRecord(r); setShowDetailModal(true); }} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title={$t.details}>
                          <Eye className="w-4 h-4" />
                        </button>
                        {canManage && (
                          <>
                            <button onClick={() => editRecord(r)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" title={$t.edit}>
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleToggleSanction(r)} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title={r.sanction_executed ? $t.markPending : $t.markExecuted}>
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            {r.incident_type === 'absence' && !r.justified && (
                              <button onClick={() => handleJustify(r.id)} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title={$t.justify}>
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                            <button onClick={() => handleConvocation(r.id)} className="p-1.5 text-purple-600 hover:bg-purple-50 rounded" title={$t.convocation}>
                              <Send className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(r.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title={$t.delete}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            {$t.page} {page} {$t.of} {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Stats détaillées */}
      {stats && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            {$t.statsDetails}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Par type */}
            <div className="border border-gray-100 rounded-lg p-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">{$t.byType}</h4>
              {Object.entries(stats.by_type || {}).length === 0 ? (
                <p className="text-xs text-gray-400">Aucune donnée</p>
              ) : (
                <div className="space-y-1.5">
                  {Object.entries(stats.by_type || {}).map(([key, count]) => (
                    <div key={key} className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 flex-1">{INCIDENT_LABELS[key] || key}</span>
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full"
                          style={{ width: `${Math.min(100, (count / (stats.total_incidents || 1)) * 100)}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-gray-900 w-6 text-right">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Par sévérité */}
            <div className="border border-gray-100 rounded-lg p-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">{$t.bySeverity}</h4>
              {Object.entries(stats.by_severity || {}).length === 0 ? (
                <p className="text-xs text-gray-400">Aucune donnée</p>
              ) : (
                <div className="space-y-1.5">
                  {Object.entries(stats.by_severity || {}).map(([key, count]) => (
                    <div key={key} className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 flex-1 capitalize">{key}</span>
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-orange-500 rounded-full"
                          style={{ width: `${Math.min(100, (count / (stats.total_incidents || 1)) * 100)}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-gray-900 w-6 text-right">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top élèves */}
            <div className="border border-gray-100 rounded-lg p-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">{$t.topStudents}</h4>
              {(stats.top_students || []).length === 0 ? (
                <p className="text-xs text-gray-400">Aucune donnée</p>
              ) : (
                <div className="space-y-1.5">
                  {stats.top_students.map((s, i) => (
                    <div key={s.student_id} className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-500 w-4">{i + 1}.</span>
                      <span className="text-xs text-gray-700 flex-1 truncate">{s.student_name}</span>
                      <span className="text-xs font-medium text-red-600">{s.count} inc.</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Téléchargement stats */}
          <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
            <span className="text-sm text-gray-500 mr-2">{$t.downloadStats}:</span>
            <button
              onClick={() => handleDownloadStats('pdf')}
              className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 text-xs border border-red-200"
            >
              <FileText className="w-3 h-3" /> PDF
            </button>
            <button
              onClick={() => handleDownloadStats('excel')}
              className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-xs border border-green-200"
            >
              <Table2 className="w-3 h-3" /> Excel
            </button>
          </div>
        </div>
      )}

      {/* Configuration listes dynamiques */}
      {canManage && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-600" />
            {$t.manageLists}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Types */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">{$t.configTypes}</h4>
                <button onClick={() => setShowConfigType(!showConfigType)} className="text-xs text-blue-600 hover:underline">
                  {showConfigType ? $t.close : $t.add}
                </button>
              </div>
              {showConfigType && (
                <div className="flex gap-2 mb-2">
                  <input
                    type="text" placeholder={$t.key}
                    value={newTypeKey} onChange={e => setNewTypeKey(e.target.value)}
                    className="flex-1 px-2 py-1 text-xs border rounded"
                  />
                  <input
                    type="text" placeholder={$t.label}
                    value={newTypeLabel} onChange={e => setNewTypeLabel(e.target.value)}
                    className="flex-1 px-2 py-1 text-xs border rounded"
                  />
                  <button onClick={addIncidentType} className="px-2 py-1 bg-blue-600 text-white text-xs rounded">
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              )}
              <div className="space-y-1 max-h-40 overflow-auto">
                {incidentTypes.map(t => (
                  <div key={t.key} className="flex items-center justify-between px-2 py-1 bg-gray-50 rounded text-xs">
                    <span>{t.label}</span>
                    <button onClick={() => removeIncidentType(t.key)} className="text-red-500 hover:text-red-700">
                      <Trash className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Sévérités */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">{$t.configSeverities}</h4>
                <button onClick={() => setShowConfigSeverity(!showConfigSeverity)} className="text-xs text-blue-600 hover:underline">
                  {showConfigSeverity ? $t.close : $t.add}
                </button>
              </div>
              {showConfigSeverity && (
                <div className="flex gap-2 mb-2">
                  <input
                    type="text" placeholder={$t.key}
                    value={newSeverityKey} onChange={e => setNewSeverityKey(e.target.value)}
                    className="flex-1 px-2 py-1 text-xs border rounded"
                  />
                  <input
                    type="text" placeholder={$t.label}
                    value={newSeverityLabel} onChange={e => setNewSeverityLabel(e.target.value)}
                    className="flex-1 px-2 py-1 text-xs border rounded"
                  />
                  <input
                    type="number" placeholder={$t.points}
                    value={newSeverityPoints} onChange={e => setNewSeverityPoints(Number(e.target.value))}
                    className="w-12 px-2 py-1 text-xs border rounded"
                  />
                  <button onClick={addSeverity} className="px-2 py-1 bg-blue-600 text-white text-xs rounded">
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              )}
              <div className="space-y-1 max-h-40 overflow-auto">
                {severityLevels.map(s => (
                  <div key={s.key} className="flex items-center justify-between px-2 py-1 bg-gray-50 rounded text-xs">
                    <span>{s.label} ({s.points} pts)</span>
                    <button onClick={() => removeSeverity(s.key)} className="text-red-500 hover:text-red-700">
                      <Trash className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Sanctions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">{$t.configSanctions}</h4>
                <button onClick={() => setShowConfigSanction(!showConfigSanction)} className="text-xs text-blue-600 hover:underline">
                  {showConfigSanction ? $t.close : $t.add}
                </button>
              </div>
              {showConfigSanction && (
                <div className="flex gap-2 mb-2">
                  <input
                    type="text" placeholder={$t.newSanction}
                    value={newSanction} onChange={e => setNewSanction(e.target.value)}
                    className="flex-1 px-2 py-1 text-xs border rounded"
                  />
                  <button onClick={addSanction} className="px-2 py-1 bg-blue-600 text-white text-xs rounded">
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              )}
              <div className="space-y-1 max-h-40 overflow-auto">
                {sanctionsList.map((s, i) => (
                  <div key={i} className="flex items-center justify-between px-2 py-1 bg-gray-50 rounded text-xs">
                    <span className="truncate">{s}</span>
                    <button onClick={() => removeSanction(i)} className="text-red-500 hover:text-red-700">
                      <Trash className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════════
          MODAL DÉTAIL INCIDENT
      ═══════════════════════════════════════════════════════════════════════════════ */}
      {showDetailModal && detailRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{$t.incidentDetails}</h3>
              <button onClick={() => setShowDetailModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-2">
              <DetailRow label={$t.student} value={detailRecord.student_name} />
              <DetailRow label="Matricule" value={detailRecord.student_matricule} />
              <DetailRow label={$t.classSelect} value={detailRecord.class_name} />
              <DetailRow label={$t.type} value={detailRecord.incident_type} />
              <DetailRow label={$t.severity} value={detailRecord.severity} />
              <DetailRow label={$t.reason} value={detailRecord.reason} />
              <DetailRow label={$t.description} value={detailRecord.description} />
              <DetailRow label={$t.sanction} value={detailRecord.sanction} />
              <DetailRow label={$t.duration} value={`${detailRecord.duration_hours}h ${detailRecord.duration_days}j`} />
              <DetailRow label={$t.justified} value={detailRecord.justified ? $t.yes : $t.no} />
              <DetailRow label={$t.justificationNote} value={detailRecord.justification_note} />
              <DetailRow label={$t.reportedBy} value={detailRecord.reported_by_name} />
              <DetailRow label={$t.createdAt} value={detailRecord.created_at ? new Date(detailRecord.created_at).toLocaleString() : null} />
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button onClick={() => setShowDetailModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm">
                {$t.close}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════════
          MODAL FORMULAIRE (CRÉATION / ÉDITION)
      ═══════════════════════════════════════════════════════════════════════════════ */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingRecord ? $t.update : $t.newIncident}
              </h3>
              <button onClick={() => { setShowModal(false); setEditingRecord(null); resetForm(); }} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Sélection classe puis élève */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{$t.classSelect}</label>
                  <SearchableSelect
                    options={classes.map(c => ({ value: c.id.toString(), label: c.name }))}
                    value={incidentClassId}
                    onChange={(val) => {
                      setIncidentClassId(val);
                      loadIncidentStudents(val);
                      setFormData(f => ({ ...f, student_id: '' }));
                    }}
                    placeholder={$t.selectClass}
                    searchPlaceholder={$t.searchClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{$t.student}</label>
                  <SearchableSelect
                    options={incidentStudents.map(s => ({ value: s.id.toString(), label: `${s.first_name} ${s.last_name} (${s.matricule})` }))}
                    value={formData.student_id}
                    onChange={(val) => setFormData(f => ({ ...f, student_id: val }))}
                    placeholder={incidentStudentsLoading ? $t.loading : $t.selectStudent}
                    searchPlaceholder={$t.searchStudent}
                    disabled={!incidentClassId || incidentStudentsLoading}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{$t.type}</label>
                  <select
                    value={formData.incident_type}
                    onChange={e => setFormData(f => ({ ...f, incident_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    {incidentTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{$t.severity}</label>
                  <select
                    value={formData.severity}
                    onChange={e => setFormData(f => ({ ...f, severity: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    {severityLevels.map(s => <option key={s.key} value={s.key}>{s.label} ({s.points} pts)</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{$t.dateStart}</label>
                <input
                  type="datetime-local"
                  value={formData.date_recorded}
                  onChange={e => setFormData(f => ({ ...f, date_recorded: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  required
                />
              </div>

              {hasDuration && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{$t.hours}</label>
                    <input
                      type="number" min={0}
                      value={formData.duration_hours}
                      onChange={e => setFormData(f => ({ ...f, duration_hours: Number(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{$t.days}</label>
                    <input
                      type="number" min={0}
                      value={formData.duration_days}
                      onChange={e => setFormData(f => ({ ...f, duration_days: Number(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{$t.reason}</label>
                <input
                  type="text"
                  value={formData.reason}
                  onChange={e => setFormData(f => ({ ...f, reason: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Motif de l'incident"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{$t.description}</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  rows={3}
                  placeholder="Description détaillée"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{$t.sanction}</label>
                <select
                  value={formData.sanction}
                  onChange={e => setFormData(f => ({ ...f, sanction: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">—</option>
                  {sanctionsList.map((s, i) => <option key={i} value={s}>{s}</option>)}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="justified"
                  checked={formData.justified}
                  onChange={e => setFormData(f => ({ ...f, justified: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="justified" className="text-sm text-gray-700">{$t.justified}</label>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingRecord(null); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  {$t.cancel}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingRecord ? $t.update : $t.save}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════════
          MODAL SAISIE RAPIDE (BULK)
      ═══════════════════════════════════════════════════════════════════════════════ */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{$t.quickEntry}</h3>
              <button onClick={() => setShowBulkModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex flex-wrap gap-3">
                <div className="w-64">
                  <label className="block text-xs font-medium text-gray-600 mb-1">{$t.classSelect}</label>
                  <SearchableSelect
                    options={classes.map(c => ({ value: c.id.toString(), label: c.name }))}
                    value={bulkClassId.toString()}
                    onChange={(val) => {
                      const cid = Number(val);
                      setBulkClassId(cid);
                      loadStudentsForBulk(cid);
                    }}
                    placeholder={$t.selectClass}
                    searchPlaceholder={$t.searchClass}
                  />
                </div>
                <div className="w-48">
                  <label className="block text-xs font-medium text-gray-600 mb-1">{$t.dateStart}</label>
                  <input
                    type="datetime-local"
                    value={bulkDate}
                    onChange={e => setBulkDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <button onClick={() => setAllBulk(0, 0)} className="px-3 py-2 bg-green-50 text-green-700 rounded-lg text-xs border border-green-200">
                    {$t.allPresent}
                  </button>
                  <button onClick={() => setAllBulk(1, 0)} className="px-3 py-2 bg-red-50 text-red-700 rounded-lg text-xs border border-red-200">
                    {$t.allAbsent}
                  </button>
                </div>
              </div>

              {studentsInClass.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">
                          <input
                            type="checkbox"
                            checked={bulkEntries.every(e => e.selected)}
                            onChange={e => setBulkEntries(prev => prev.map(entry => ({ ...entry, selected: e.target.checked })))}
                            className="w-4 h-4"
                          />
                        </th>
                        <th className="px-3 py-2 text-left">{$t.student}</th>
                        <th className="px-3 py-2 text-left">{$t.hours}</th>
                        <th className="px-3 py-2 text-left">{$t.days}</th>
                        <th className="px-3 py-2 text-left">{$t.justified}</th>
                        <th className="px-3 py-2 text-left">{$t.reason}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {bulkEntries.map((entry, idx) => {
                        const student = studentsInClass.find(s => s.id === entry.student_id);
                        return (
                          <tr key={entry.student_id} className={entry.selected ? 'bg-blue-50' : ''}>
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={entry.selected}
                                onChange={e => {
                                  const newEntries = [...bulkEntries];
                                  newEntries[idx].selected = e.target.checked;
                                  setBulkEntries(newEntries);
                                }}
                                className="w-4 h-4"
                              />
                            </td>
                            <td className="px-3 py-2">{student ? `${student.first_name} ${student.last_name}` : '—'}</td>
                            <td className="px-3 py-2">
                              <input
                                type="number" min={0}
                                value={entry.duration_hours}
                                onChange={e => {
                                  const newEntries = [...bulkEntries];
                                  newEntries[idx].duration_hours = Number(e.target.value);
                                  setBulkEntries(newEntries);
                                }}
                                className="w-16 px-2 py-1 border rounded text-xs"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number" min={0}
                                value={entry.duration_days}
                                onChange={e => {
                                  const newEntries = [...bulkEntries];
                                  newEntries[idx].duration_days = Number(e.target.value);
                                  setBulkEntries(newEntries);
                                }}
                                className="w-16 px-2 py-1 border rounded text-xs"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={entry.justified}
                                onChange={e => {
                                  const newEntries = [...bulkEntries];
                                  newEntries[idx].justified = e.target.checked;
                                  setBulkEntries(newEntries);
                                }}
                                className="w-4 h-4"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={entry.reason}
                                onChange={e => {
                                  const newEntries = [...bulkEntries];
                                  newEntries[idx].reason = e.target.value;
                                  setBulkEntries(newEntries);
                                }}
                                className="w-full px-2 py-1 border rounded text-xs"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button onClick={() => setShowBulkModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
                  {$t.cancel}
                </button>
                <button
                  onClick={handleBulkSave}
                  disabled={loading}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {$t.save}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════════
          MODAL CONFIRMATION SUPPRESSION
      ═══════════════════════════════════════════════════════════════════════════════ */}
      {deleteConfirm.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-red-700 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                {deleteConfirm.title}
              </h3>
            </div>
            <div className="p-4 space-y-2">
              <p className="text-sm text-gray-700">{deleteConfirm.message}</p>
              {deleteConfirm.subMessage && (
                <p className="text-xs text-gray-500">{deleteConfirm.subMessage}</p>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={closeDeleteConfirm} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
                {$t.cancel}
              </button>
              <button
                onClick={() => { deleteConfirm.onConfirm(); }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
              >
                {deleteConfirm.confirmBtnText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}